import { useCallback, useEffect, useRef, useState } from 'react';

interface ResizeHandleProps {
  /** Current width of the panel being resized, in px. */
  width: number;
  /** Smallest allowed width. Drag can't go below this. */
  min: number;
  /** Largest allowed width. Drag can't go above this. */
  max: number;
  /**
   * Fired continuously while dragging with the live (clamped) width — wire
   * this to local component state so the panel tracks the cursor smoothly.
   */
  onResize: (width: number) => void;
  /**
   * Fired once on mouse-up with the final (clamped) width — wire this to the
   * persisted store so the chosen width survives reloads.
   */
  onResizeEnd?: (width: number) => void;
  /**
   * Which edge of the panel this handle sits on. Determines drag direction:
   * a handle on the panel's RIGHT edge grows the panel as the cursor moves
   * right (delta added); on the LEFT edge it grows as the cursor moves left
   * (delta subtracted). Defaults to 'right' (the common left-rail case).
   */
  edge?: 'right' | 'left';
}

/**
 * A thin, draggable vertical seam that sits on a panel's border. Hovering it
 * shows the ↔ (col-resize) cursor and reveals a 2px accent rule; dragging
 * resizes the adjacent panel within [min, max]. It deliberately renders as a
 * zero-footprint overlay (negative margins) so it overlaps the panel's own
 * 1px border instead of adding layout width — the seam IS the border.
 *
 * Used by the shell to make the left nav rail resizable; reusable for any
 * fixed-width list pane that wants the same affordance.
 */
export function ResizeHandle({
  width,
  min,
  max,
  onResize,
  onResizeEnd,
  edge = 'right',
}: ResizeHandleProps) {
  const [active, setActive] = useState(false);
  const [hovered, setHovered] = useState(false);
  // Captured at mousedown so the drag is relative to where the grab started,
  // not absolute cursor position — lets you grab the seam anywhere.
  const start = useRef<{ x: number; w: number } | null>(null);
  const latest = useRef(width);

  const clamp = useCallback((w: number) => Math.max(min, Math.min(max, w)), [min, max]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      start.current = { x: e.clientX, w: width };
      latest.current = width;
      setActive(true);
    },
    [width],
  );

  useEffect(() => {
    if (!active) return;

    const move = (e: MouseEvent) => {
      if (!start.current) return;
      const dx = e.clientX - start.current.x;
      const delta = edge === 'right' ? dx : -dx;
      const next = clamp(start.current.w + delta);
      latest.current = next;
      onResize(next);
    };
    const up = () => {
      setActive(false);
      start.current = null;
      onResizeEnd?.(latest.current);
    };

    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    // While dragging, force the resize cursor everywhere and kill text
    // selection — otherwise a fast drag selects nav labels and the cursor
    // flickers to the I-beam as it passes over text.
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [active, clamp, edge, onResize, onResizeEnd]);

  const lit = active || hovered;

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panel"
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        flexShrink: 0,
        // 7px-wide invisible hit target straddling the 1px border. Negative
        // right margin pulls it back so it doesn't widen the flex row — the
        // seam overlaps the panel border instead of pushing content over.
        width: 7,
        marginLeft: -3,
        marginRight: -4,
        cursor: 'col-resize',
        zIndex: 5,
        // Smooth the accent rule fading in/out, but never animate during an
        // active drag (the rule should feel pinned to the cursor).
        touchAction: 'none',
      }}
    >
      {/* The visible rule — a hairline at rest, a 2px accent on hover/drag. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          // Center the rule on the panel's own border (which lives at the
          // seam's left edge after the negative margins).
          left: 3,
          width: lit ? 2 : 1,
          background: lit ? 'var(--accent)' : 'transparent',
          transition: active ? 'none' : 'background 120ms ease, width 120ms ease',
        }}
      />
    </div>
  );
}
